import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import userProfile from "../assets/user-profile.png";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { userService, ServiceOnMount, profileService } from "../services";
import { Skeleton } from "../components/ui/skeleton";
import useUserStore2 from "../stores/userStore2";
import ProfileCard from "../components/ProfileCard";
import ShortenedText from "../components/ShortenedText";
import { UserIcon } from "lucide-react";

const ViewOwnAccount = () => {
  const { userId } = useParams(); // To get the community ID from the URL
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("general");
  const [reload, setReload] = useState(false);

  const roleMapping = {
    A: "Super Admin",
    CA: "Community Admin",
    GL: "Group Leader",
    C: "Collector",
    POCA: "POC Attendant",
    U: "User",
  };

  const dataMap = (item) => ({
    userID: item._id,
    username: item.name,
    firstName: item.first_name,
    lastName: item.last_name,
    role: roleMapping[item.role] || "Unknown Role",
    email: item.email,
    nric: item.nric,
    profStatus: item.status,
    mobileNumber: item.phone,
    createDate: item.create_date
      ? new Date(item.create_date).toLocaleDateString()
      : "Not Available",
    lastLogin: item.last_login
      ? new Date(item.last_login).toLocaleString()
      : "Not Available",
  });

  const getUserDetails = ServiceOnMount(
    () => userService.getUserDetails(userId),
    dataMap
  );
  const userData = getUserDetails.data?.[0];

  const getUserProfiles = ServiceOnMount(
    () => profileService.getUserProfiles(userId),
    (item) => ({
      profileId: item._id,
      name: item.name,
      dateCreated: new Date(item.create_date).toLocaleDateString(),
      status: item.type === "P" ? "Primary" : "", // Updated status logic
      variant: "teal", // You can dynamically assign variants if needed
      profStatus: item.status,
    }),
    [reload]
  );

  const { updateUser, user } = useUserStore2((state) => state);
  const profiles = getUserProfiles.data || [];
  const profilePicture = user?.profilePicture;
  const primaryProfileId = user?.primaryProfile?.id;
  const currentProfileId = user?.currentProfile?.id || null;

  console.log("user content: ", user);

  console.log("profiles: ", profiles);

  useEffect(() => {
    if (userData) {
      console.log(`ViewAccount userData:`, userData);
      updateUser({
        createDate: userData.createDate,
        lastLogin: userData.lastLogin,
      });
    }
  }, [userData]);

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
  };

  const handleEdit = () => {
    navigate(`/edit-account/${userId}`);
  };

  useEffect(() => {
    console.log("Profiles State:", profiles);
  }, [profiles]);

  return (
    <div className="flex flex-col md:flex-row min-h-screen">
      <div className="flex-1 flex flex-col">
        <div className="p-8 flex flex-col">
          <div className="bg-white shadow p-4 pb-1 rounded-lg">
            <div className="flex flex-col md:flex-row justify-between md:items-center gap-4 md:gap-0 mb-6">
              {/* Left: User Info */}
              <div className="flex flex-col sm:flex-row sm:items-start md:items-center gap-4">
                <div className="h-20 w-20 rounded-full bg-gray-200 flex items-center justify-center">
                  {profilePicture ? (
                    <img
                      src={profilePicture}
                      alt="User Profile"
                      className="h-full w-full rounded-full object-cover"
                      onError={(e) => {
                        e.target.src = "";
                        e.target.className = "hidden"; // Hide the broken image
                      }}
                    />
                  ) : (
                    <UserIcon className="h-6 w-6 text-gray-500" /> // Using Lucide-react icon
                  )}
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-[#111827]">
                    {user ? user.name : <Skeleton className="w-40 h-6" />}
                  </h1>
                  <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row sm:space-x-8 space-y-4 sm:space-y-0">
                    <div>
                      <div className="text-sm font-semibold text-[#6B7280]">
                        Date Created
                      </div>
                      <div className="text-sm text-[#111827]">
                        {user ? (
                          user.createDate
                        ) : (
                          <Skeleton className="w-24 h-4" />
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-[#6B7280]">
                        Last Login
                      </div>
                      <div className="text-sm text-[#111827]">
                        {user ? (
                          user.lastLogin
                        ) : (
                          <Skeleton className="w-32 h-4" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Action Buttons */}
              <div className="flex flex-col md:flex-row items-center md:items-start gap-2 w-full md:w-auto">
                <Button className="w-full md:w-auto" onClick={handleEdit}>
                  Edit
                </Button>
              </div>
            </div>
          </div>

          <div className="flex">
            <button
              onClick={() => handleTabSwitch("general")}
              className={`relative mt-5 rounded-tl-xl px-3 pr-10 py-3 text-[14px] font-bold ${
                activeTab === "general"
                  ? "text-[#111827] bg-white"
                  : "text-[#14B8A6] bg-[#F0FDFA]"
              }`}
              style={{
                clipPath: "polygon(0 0, 70% 0, 100% 100%, 0% 100%)",
              }}
            >
              General Info
            </button>
            <button
              onClick={() => handleTabSwitch("profile")}
              className={`relative mt-5 rounded-tl-xl px-3 pr-10 py-3 text-[14px] font-bold ${
                activeTab === "profile"
                  ? "text-[#111827] bg-white"
                  : "text-[#14B8A6] bg-[#F0FDFA]"
              }`}
              style={{
                clipPath: "polygon(0 0, 70% 0, 100% 100%, 0% 100%)",
              }}
            >
              Profile Info
            </button>
          </div>

          <div className="grid grid-cols-1 gap-8 p-6 mt-0 bg-white shadow rounded-r-lg rounded-b-lg border-gray-300">
            {activeTab === "general" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>First Name</Label>
                  <p className="w-full p-2 border-b-2 border-gray-300 text-sm">
                    {user ? user.firstName : <Skeleton className="w-40 h-4" />}
                  </p>
                </div>
                <div>
                  <Label>Last Name</Label>
                  <p className="w-full p-2 border-b-2 border-gray-300 text-sm">
                    {user ? user.lastName : <Skeleton className="w-40 h-4" />}
                  </p>
                </div>
                <div>
                  <Label>Role</Label>
                  <p className="w-full p-2 border-b-2 border-gray-300 text-sm">
                    {user?.role ? (
                      user.role || "Unknown Role"
                    ) : (
                      <Skeleton className="w-32 h-4" />
                    )}
                  </p>
                </div>
                <div>
                  <Label>Email</Label>
                  <p className="w-full p-2 border-b-2 border-gray-300 text-sm">
                    {user ? user.email : <Skeleton className="w-60 h-4" />}
                  </p>
                </div>
                <div>
                  <Label>NRIC</Label>
                  <p className="w-full p-2 border-b-2 border-gray-300 text-sm">
                    {user ? user.nric : <Skeleton className="w-40 h-4" />}
                  </p>
                </div>
                <div>
                  <Label>Mobile Number</Label>
                  <p className="w-full p-2 border-b-2 border-gray-300 text-sm">
                    {user ? user.phone : <Skeleton className="w-40 h-4" />}
                  </p>
                </div>
              </div>
            ) : (
              <>
                {" "}
                <div className="grid grid-cols-1 md820:grid-cols-3 md:grid-cols-2 gap-6">
                  {profiles.length > 0 ? (
                    profiles.map((profile, index) => (
                      <div>
                        <ProfileCard
                          key={profile.profileId}
                          profileId={<ShortenedText text={profile.profileId} />}
                          name={profile.name}
                          dateCreated={profile.dateCreated}
                          status={
                            profile.profileId === primaryProfileId
                              ? "Primary"
                              : ""
                          }
                          variant={
                            ["teal", "green", "orange", "purple", "indigo"][
                              index % 5
                            ]
                          }
                          selected={profile.profileId === currentProfileId}
                          profStatus={profile.profStatus}
                        />
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500">No profiles found.</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewOwnAccount;
